/**
 * Астрологические утилиты
 * Расчёт фазы Луны, знака Солнца, лунного дня
 */

export interface AstroInfo {
  moonPhase: string;
  moonPhaseIcon: string;
  sunSign: string;
  sunSignIcon: string;
  lunarDay: number;
}

const ZODIAC_SIGNS = [
  { name: 'Овен', icon: '♈', startDay: 21, month: 3 },
  { name: 'Телец', icon: '♉', startDay: 20, month: 4 },
  { name: 'Близнецы', icon: '♊', startDay: 21, month: 5 },
  { name: 'Рак', icon: '♋', startDay: 21, month: 6 },
  { name: 'Лев', icon: '♌', startDay: 23, month: 7 },
  { name: 'Дева', icon: '♍', startDay: 23, month: 8 },
  { name: 'Весы', icon: '♎', startDay: 23, month: 9 },
  { name: 'Скорпион', icon: '♏', startDay: 23, month: 10 },
  { name: 'Стрелец', icon: '♐', startDay: 22, month: 11 },
  { name: 'Козерог', icon: '♑', startDay: 22, month: 12 },
  { name: 'Водолей', icon: '♒', startDay: 20, month: 1 },
  { name: 'Рыбы', icon: '♓', startDay: 19, month: 2 },
];

const MOON_PHASES = [
  { name: 'Новолуние', icon: '🌑', threshold: 0.03 },
  { name: 'Растущая луна', icon: '🌒', threshold: 0.25 },
  { name: 'Первая четверть', icon: '🌓', threshold: 0.28 },
  { name: 'Растущая луна', icon: '🌔', threshold: 0.47 },
  { name: 'Полнолуние', icon: '🌕', threshold: 0.53 },
  { name: 'Убывающая луна', icon: '🌖', threshold: 0.72 },
  { name: 'Последняя четверть', icon: '🌗', threshold: 0.75 },
  { name: 'Убывающая луна', icon: '🌘', threshold: 0.97 },
  { name: 'Новолуние', icon: '🌑', threshold: 1.0 },
];

/**
 * Получить знак Солнца по дате
 */
export function getSunSign(date: Date = new Date()): { name: string; icon: string } {
  const day = date.getDate();
  const month = date.getMonth() + 1;

  for (let i = 0; i < ZODIAC_SIGNS.length; i++) {
    const sign = ZODIAC_SIGNS[i];
    const nextSign = ZODIAC_SIGNS[(i + 1) % ZODIAC_SIGNS.length];

    if (sign.month === month && day >= sign.startDay) {
      return { name: sign.name, icon: sign.icon };
    }

    if (nextSign.month === month && day < nextSign.startDay) {
      return { name: sign.name, icon: sign.icon };
    }
  }

  // Козерог (конец декабря - начало января)
  return { name: 'Козерог', icon: '♑' };
}

/**
 * Рассчитать фазу Луны (упрощённый алгоритм)
 * Основано на известном новолунии 2000-01-06
 */
export function getMoonPhase(date: Date = new Date()): { name: string; icon: string; illumination: number } {
  const knownNewMoon = new Date(Date.UTC(2000, 0, 6, 18, 14, 0));
  const lunarCycle = 29.53058867; // дней

  const diffMs = date.getTime() - knownNewMoon.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  const currentPhase = (diffDays % lunarCycle) / lunarCycle;

  // Найти ближайшую фазу
  for (const phase of MOON_PHASES) {
    if (currentPhase < phase.threshold + 0.02) {
      const illumination = Math.round(currentPhase * 100);
      return { name: phase.name, icon: phase.icon, illumination };
    }
  }

  return { name: 'Новолуние', icon: '🌑', illumination: 0 };
}

/**
 * Рассчитать лунный день (упрощённо)
 */
export function getLunarDay(date: Date = new Date()): number {
  const knownNewMoon = new Date(Date.UTC(2000, 0, 6, 18, 14, 0));
  const lunarCycle = 29.53058867;

  const diffMs = date.getTime() - knownNewMoon.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  const lunarDay = Math.floor((diffDays % lunarCycle) + 1);

  return Math.min(lunarDay, 30);
}

/**
 * Получить полную астрологическую информацию
 */
export function getAstroInfo(date: Date = new Date()): AstroInfo {
  const sunSign = getSunSign(date);
  const moonPhase = getMoonPhase(date);
  const lunarDay = getLunarDay(date);

  return {
    moonPhase: moonPhase.name,
    moonPhaseIcon: moonPhase.icon,
    sunSign: sunSign.name,
    sunSignIcon: sunSign.icon,
    lunarDay,
  };
}

/**
 * Форматировать дату на русском
 */
export function formatDateRu(date: Date = new Date()): string {
  const months = [
    'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
  ];

  const weekdays = [
    'воскресенье', 'понедельник', 'вторник', 'среда',
    'четверг', 'пятница', 'суббота'
  ];

  const day = date.getDate();
  const month = months[date.getMonth()];
  const weekday = weekdays[date.getDay()];

  return `${day} ${month}, ${weekday}`;
}

/**
 * Краткое описание дня на основе фазы Луны и знака Солнца
 */
export function getDayDescription(date: Date = new Date()): string {
  const astro = getAstroInfo(date);
  const moonPhase = astro.moonPhase;
  const sunSign = astro.sunSign;

  // Описания на основе фазы Луны
  const descriptions: Record<string, string> = {
    'Новолуние': 'День начинаний. Хорошее время для планирования.',
    'Растущая луна': 'Энергия растёт. Время для активных действий.',
    'Первая четверть': 'День решительности. Преодолевайте препятствия.',
    'Полнолуние': 'Пик энергии. Эмоции усилены, интуиция сильна.',
    'Последняя четверть': 'Время завершения. Отпускайте старое.',
    'Убывающая луна': 'Энергия снижается. Время для анализа и отдыха.',
  };

  return descriptions[moonPhase] || 'Обычный день. Следуйте своему плану.';
}

/**
 * Получить информацию для календаря (3 дня)
 */
export function getCalendarDays(): Array<{
  date: Date;
  label: string;
  dateStr: string;
  sunSign: string;
  sunSignIcon: string;
  moonPhase: string;
  moonPhaseIcon: string;
  lunarDay: number;
  description: string;
}> {
  const today = new Date();
  const days = [];

  for (let i = 0; i < 3; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    const astro = getAstroInfo(date);
    const labels = ['Сегодня', 'Завтра', 'Послезавтра'];

    days.push({
      date,
      label: labels[i],
      dateStr: formatDateRu(date),
      sunSign: astro.sunSign,
      sunSignIcon: astro.sunSignIcon,
      moonPhase: astro.moonPhase,
      moonPhaseIcon: astro.moonPhaseIcon,
      lunarDay: astro.lunarDay,
      description: getDayDescription(date),
    });
  }

  return days;
}
